import { useCallback, useRef } from 'react'

import { TokenWithLogo } from '@cowprotocol/common-const'
import { isTruthy } from '@cowprotocol/common-utils'
import { getAddressKey } from '@cowprotocol/cow-sdk'
import { Token } from '@cowprotocol/currency'
import { fetchTokenFromBlockchain, TokensByAddress, useAddUserToken, useTokensByAddressMap } from '@cowprotocol/tokens'
import { useWalletInfo } from '@cowprotocol/wallet'

import { useConfig } from 'wagmi'

import { getTokenFromMapping } from 'utils/orderUtils/getTokenFromMapping'

export function useTokensForOrdersList(): (tokensToFetch: string[], signal?: AbortSignal) => Promise<TokensByAddress> {
  const config = useConfig()
  const { chainId } = useWalletInfo()
  const allTokens = useTokensByAddressMap()
  const addUserTokens = useAddUserToken()

  const getToken = useCallback(
    async (address: string, signal?: AbortSignal) => {
      if (signal?.aborted) return null
      if (!config) return null
      // Cast to satisfy fetchTokenFromBlockchain's Config type (tokens lib may resolve a different wagmi/viem copy)
      const token = await fetchTokenFromBlockchain(
        address,
        chainId,
        config as Parameters<typeof fetchTokenFromBlockchain>[2],
      ).then(TokenWithLogo.fromToken)
      return signal?.aborted ? null : token
    },
    [chainId, config],
  )

  // Using a ref to store allTokens to avoid re-fetching when new tokens are added
  // but still use the latest whenever the callback is invoked
  const allTokensRef = useRef(allTokens)
  // Updated on every change
  // eslint-disable-next-line react-hooks/refs
  allTokensRef.current = allTokens

  return useCallback(
    async (_tokensToFetch: string[], signal?: AbortSignal) => {
      if (signal?.aborted) return allTokensRef.current

      const tokens = allTokensRef.current

      const tokensToFetch = _tokensToFetch.reduce<string[]>((acc, token) => {
        const tokenKey = getAddressKey(token)

        if (!getTokenFromMapping(tokenKey, chainId, tokens)) {
          acc.push(tokenKey)
        }
        return acc
      }, [])

      const fetchedTokens = await _fetchTokens(tokensToFetch, getToken, signal)
      if (signal?.aborted) return tokens

      // Add fetched tokens to the user-added tokens store to avoid re-fetching them
      const tokensToAdd = Object.values(fetchedTokens).filter(isTruthy)

      if (tokensToAdd.length > 0) {
        // FIXME: this might be a cause of the problem when we get listed tokens as user-added tokens
        // Since we use allTokensRef which is not a part of the hooks deps, there might be a race condition
        console.log('Add missing tokens from orders as user-added: ', tokensToAdd)
        addUserTokens(tokensToAdd)
      }

      // Merge fetched tokens with what's currently loaded
      return { ...tokens, ...fetchedTokens }
    },
    [chainId, getToken, addUserTokens],
  )
}

async function _fetchTokens(
  tokensToFetch: string[],
  getToken: (address: string, signal?: AbortSignal) => Promise<Token | null>,
  signal?: AbortSignal,
): Promise<TokensByAddress> {
  if (tokensToFetch.length === 0 || signal?.aborted) {
    return {}
  }

  const promises = tokensToFetch.map((address) => getToken(address, signal))
  const settledPromises = await Promise.allSettled(promises)
  if (signal?.aborted) return {}

  return settledPromises.reduce<TokensByAddress>((acc, promiseResult) => {
    if (promiseResult.status === 'fulfilled' && promiseResult.value) {
      const { chainId, address, decimals, symbol, name } = promiseResult.value
      const addressKey = getAddressKey(address)

      acc[addressKey] = new TokenWithLogo(undefined, chainId, addressKey, decimals, symbol, name)
    }
    return acc
  }, {})
}
